import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Leaf } from 'lucide-react';
import { Card } from '../Card';
import { cn } from '../../../utils/cn';
import { useReducedMotion } from '../../../hooks/useReducedMotion';
import { DotRating, getDifficultyColor } from './DotRating';
import {
  translateCategory,
  getDifficultyLabel,
} from '../../../pages/PlantOPedia/utils/plantopedia-utils';
import type { SpeciesCardProps } from './SpeciesCard.types';

export const SpeciesTile: React.FC<SpeciesCardProps> = ({ species, onClick, className }) => {
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`/plant-o-pedia/species/${species.id}`);
    }
  };

  const difficulty = species.difficulty_level || (species as any).difficulty || 3;

  return (
    <motion.div
      initial={reducedMotion ? false : { opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={reducedMotion ? {} : { scale: 1.03 }}
      onClick={handleClick}
      role="link"
      tabIndex={0}
      aria-label={`${species.name} — ${translateCategory(species.plant_category || species.plant_type)}, obtížnost ${getDifficultyLabel(difficulty)}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        'cursor-pointer focus-visible:outline-2 focus-visible:outline-green-500 focus-visible:outline-offset-2 rounded-2xl',
        className,
      )}
    >
      <Card
        variant="glass"
        padding="none"
        className="h-full hover:border-green-500/30 transition-colors overflow-hidden group"
      >
        <div className="flex flex-col items-center text-center p-4 gap-2">
          {/* Icon */}
          {species.icon ? (
            <div className="relative w-12 h-12">
              <img
                src={species.icon}
                alt={species.name}
                className="w-12 h-12 rounded-xl object-cover shadow-sm border border-white dark:border-zinc-700"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  if (target.nextElementSibling) {
                    (target.nextElementSibling as HTMLElement).style.display = 'flex';
                  }
                }}
              />
              <div className="hidden items-center justify-center w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                <Leaf size={24} />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
              <Leaf size={24} />
            </div>
          )}

          {/* Name */}
          <h3 className="text-sm font-bold text-gray-900 dark:text-white line-clamp-1 w-full group-hover:text-green-600 transition-colors">
            {species.name}
          </h3>

          {/* Category */}
          <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-white/80 dark:bg-black/50 backdrop-blur-sm text-gray-600 dark:text-zinc-300 font-medium capitalize border border-white/20">
            {translateCategory(species.plant_category || species.plant_type)}
          </span>

          {/* Difficulty */}
          <DotRating
            value={difficulty}
            activeColor={getDifficultyColor(difficulty)}
            label="Obtížnost"
          />
        </div>
      </Card>
    </motion.div>
  );
};
